/**
 * HeroPreview — premium live preview top sekcji.
 *
 * KLUCZ produktu: "instant WOW". To NIE jest techniczny editor —
 * to emotional first-impression preview, który ma wyglądać jak gotowa strona.
 *
 * Props:
 *  - config: { image_url, label, headline, subheading, cta_primary, cta_secondary, style, tags }
 *  - device: 'desktop' | 'mobile' (toggle preview viewport)
 *  - compact: boolean (kafelek w galerii templatek vs full-size preview)
 */
import { useRef } from 'react';
import {
  Dog, Shield, Home, PawPrint, Star, Medal, Heart, Sparkles, ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';

const TAG_ICON = {
  dog: Dog, shield: Shield, home: Home, paw: PawPrint,
  star: Star, medal: Medal, heart: Heart, sparkles: Sparkles,
};

const FONT_STACK = {
  'montserrat-bold': "'Montserrat', system-ui, sans-serif",
  'montserrat-extrabold': "'Montserrat', system-ui, sans-serif",
  'playfair-bold': "'Playfair Display', Georgia, serif",
};

const FONT_WEIGHT = {
  'montserrat-bold': 700,
  'montserrat-extrabold': 800,
  'playfair-bold': 700,
};

function overlayBg(overlay, strength) {
  const a = Math.max(0, Math.min(100, strength)) / 100;
  switch (overlay) {
    case 'none': return 'transparent';
    case 'dark': return `rgba(0,0,0,${a * 0.65})`;
    case 'gradient_violet':
      return `linear-gradient(135deg, rgba(20,8,40,${a * 0.85}) 0%, rgba(124,92,230,${a * 0.45}) 100%)`;
    case 'gradient_orange':
      return `linear-gradient(135deg, rgba(40,18,8,${a * 0.85}) 0%, rgba(249,115,22,${a * 0.35}) 100%)`;
    case 'gradient_bottom':
    default:
      return `linear-gradient(to top, rgba(0,0,0,${a * 0.92}) 0%, rgba(0,0,0,${a * 0.55}) 35%, rgba(0,0,0,0) 75%)`;
  }
}

function ctaClasses(style, accent, kind = 'primary') {
  // style: 'filled' | 'outline' | 'pill'
  const base = 'inline-flex items-center justify-center gap-2 font-semibold transition-all whitespace-nowrap';
  const isPrimary = kind === 'primary';
  if (style === 'outline') {
    return `${base} border-2 px-5 py-3 rounded-lg backdrop-blur-sm ${isPrimary ? 'text-white' : 'text-white/95'}`;
  }
  if (style === 'pill') {
    return `${base} px-6 py-3 rounded-full ${isPrimary ? 'text-white shadow-2xl' : 'text-white/95 border border-white/30 backdrop-blur-sm'}`;
  }
  // filled (default)
  return `${base} px-5 py-3 rounded-xl ${isPrimary ? 'text-white shadow-2xl' : 'text-white/95 border border-white/25 backdrop-blur-sm bg-white/[0.06]'}`;
}

function ctaInlineStyle(style, accent, kind = 'primary') {
  const isPrimary = kind === 'primary';
  if (style === 'outline') {
    return isPrimary
      ? { borderColor: accent, color: accent, backgroundColor: 'rgba(0,0,0,0.18)' }
      : { borderColor: 'rgba(255,255,255,0.55)' };
  }
  if (isPrimary) {
    return { backgroundColor: accent, boxShadow: `0 16px 40px -10px ${accent}66` };
  }
  return {};
}

export default function HeroPreview({ config, device = 'desktop', compact = false, draggable = false, onPositionChange }) {
  const c = config || {};
  const s = c.style || {};
  const accent = s.accent_color || '#7C5CE6';
  const isMobile = device === 'mobile';
  const overlayCss = overlayBg(s.overlay || 'gradient_bottom', s.overlay_strength ?? 55);
  const align = s.text_align || 'left';
  const alignClass = align === 'center' ? 'items-center text-center' : align === 'right' ? 'items-end text-right' : 'items-start text-left';

  // Image positioning (FB-style cover)
  const posX = typeof s.image_position_x === 'number' ? s.image_position_x : 50;
  const posY = typeof s.image_position_y === 'number' ? s.image_position_y : 50;
  const zoom = typeof s.image_zoom === 'number' ? Math.max(1, Math.min(3, s.image_zoom)) : 1;

  // Mobile-safe automation: scale down sizes & enforce stacking
  // Compact mode (template thumbs) shrinks even more aggressively.
  const scale = compact ? 0.22 : isMobile ? 0.5 : 1;
  const headlineSize = Math.max(11, Math.round((s.headline_size || 72) * scale));
  const subSize = Math.max(9, Math.round((s.sub_size || 22) * scale));
  const labelSize = Math.max(9, Math.round((s.label_size || 14) * (compact ? 0.7 : scale)));

  const tags = Array.isArray(c.tags) ? c.tags : [];
  const fontFamily = FONT_STACK[s.headline_font || 'montserrat-extrabold'];
  const fontWeight = FONT_WEIGHT[s.headline_font || 'montserrat-extrabold'];

  // Drag state — pointer events unify mouse + touch
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const onPointerDown = (e) => {
    if (!draggable || !onPositionChange || !c.image_url) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startPosX: posX, startPosY: posY,
      rectW: rect.width, rectH: rect.height,
    };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    // Drag right reveals more of left side → posX decreases.
    // Sensitivity scaled by zoom (higher zoom = finer control).
    const dx = ((e.clientX - d.startX) / d.rectW) * 100 / zoom;
    const dy = ((e.clientY - d.startY) / d.rectH) * 100 / zoom;
    const nx = Math.max(0, Math.min(100, d.startPosX - dx));
    const ny = Math.max(0, Math.min(100, d.startPosY - dy));
    onPositionChange({ image_position_x: nx, image_position_y: ny });
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-2xl bg-[#0a0a14] border border-white/10 shadow-2xl shadow-black/40 ${compact ? '' : 'w-full'} ${draggable && c.image_url ? 'cursor-move select-none' : ''}`}
      style={{
        aspectRatio: isMobile ? '9/16' : '16/9',
        maxHeight: compact ? 220 : 'unset',
        touchAction: draggable ? 'none' : undefined,
      }}
      data-testid={compact ? 'hero-preview-compact' : 'hero-preview-live'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Background image with subtle Ken-Burns motion + FB-style positioning */}
      {c.image_url ? (
        <motion.img
          key={c.image_url}
          src={c.image_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{
            objectPosition: `${posX}% ${posY}%`,
            transform: `scale(${zoom})`,
            transformOrigin: `${posX}% ${posY}%`,
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/40 via-[#0a0a14] to-[#0a0a14] flex items-center justify-center">
          <div className="text-center px-6">
            <Sparkles className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-white/40 text-xs">Wybierz zdjęcie żeby zobaczyć podgląd</div>
          </div>
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0" style={{ background: overlayCss }} />

      {/* Logo overlay (top-left, premium feel) */}
      {c.logo_url && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: compact ? 0 : 0.1 }}
          className="absolute top-0 left-0 z-10"
          style={{
            padding: isMobile ? '12px' : compact ? '10px' : '24px',
          }}
          data-testid="preview-logo"
        >
          <div
            className="rounded-xl bg-white/95 backdrop-blur-md shadow-lg flex items-center justify-center overflow-hidden ring-1 ring-white/30"
            style={{
              width: compact ? 28 : isMobile ? 44 : 72,
              height: compact ? 28 : isMobile ? 44 : 72,
              padding: compact ? 2 : 6,
            }}
          >
            <img src={c.logo_url} alt="Logo" className="w-full h-full object-contain" draggable={false} />
          </div>
        </motion.div>
      )}

      {/* Content */}
      <div
        className={`absolute inset-0 flex flex-col justify-end ${alignClass}`}
        style={{
          padding: isMobile ? '20px 18px 22px' : compact ? '22px' : '40px 48px 48px',
        }}
      >
        {c.label && (
          <motion.div
            key={`label-${c.label}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: compact ? 0 : 0.08 }}
            className="uppercase tracking-[0.18em] font-semibold mb-2"
            style={{ color: accent, fontSize: `${labelSize}px` }}
            data-testid="preview-label"
          >
            {c.label}
          </motion.div>
        )}
        {c.headline && (
          <motion.div
            key={`h-${c.headline}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: compact ? 0.05 : 0.18 }}
            className="text-white leading-[1.04]"
            style={{
              fontFamily,
              fontWeight,
              fontSize: `${headlineSize}px`,
              maxWidth: align === 'center' ? '90%' : '78%',
              textShadow: '0 4px 24px rgba(0,0,0,0.45)',
            }}
            data-testid="preview-headline"
          >
            {c.headline}
          </motion.div>
        )}
        {c.subheading && (
          <motion.div
            key={`s-${c.subheading}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: compact ? 0.1 : 0.28 }}
            className="text-white/90 mt-3 leading-snug"
            style={{
              fontSize: `${subSize}px`,
              maxWidth: align === 'center' ? '88%' : '64%',
              textShadow: '0 2px 12px rgba(0,0,0,0.5)',
            }}
            data-testid="preview-subheading"
          >
            {c.subheading}
          </motion.div>
        )}

        {/* Tags */}
        {tags.length > 0 && !compact && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.36 }}
            className={`mt-4 sm:mt-5 flex flex-wrap gap-x-${isMobile ? 3 : 5} gap-y-2 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}
            style={{ fontSize: isMobile ? 10 : 13 }}
            data-testid="preview-tags"
          >
            {tags.slice(0, 3).map((tg, i) => {
              const Icon = TAG_ICON[tg.icon] || Dog;
              return (
                <div key={i} className="inline-flex items-center gap-1.5 text-white/85">
                  <Icon className="flex-shrink-0" style={{ width: isMobile ? 12 : 16, height: isMobile ? 12 : 16, color: accent }} />
                  <span>{tg.text}</span>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* CTAs — mobile-safe stacking — hidden in compact (template thumbnails) */}
        {(c.cta_primary || c.cta_secondary) && !compact && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.42 }}
            className={`mt-${isMobile ? 4 : 6} flex ${isMobile ? 'flex-col w-full' : 'flex-row flex-wrap'} gap-2.5 ${align === 'center' ? (isMobile ? '' : 'justify-center') : align === 'right' ? 'justify-end' : ''}`}
            style={{ fontSize: isMobile ? 12 : 14 }}
            data-testid="preview-ctas"
          >
            {c.cta_primary?.label && (
              <motion.button
                whileHover={{ scale: 1.04, boxShadow: `0 22px 50px -8px ${accent}88` }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                type="button"
                className={ctaClasses(s.cta_style || 'filled', accent, 'primary')}
                style={{
                  ...ctaInlineStyle(s.cta_style || 'filled', accent, 'primary'),
                  padding: isMobile ? '10px 16px' : undefined,
                }}
              >
                <PawPrint style={{ width: isMobile ? 13 : 16, height: isMobile ? 13 : 16 }} />
                {c.cta_primary.label}
              </motion.button>
            )}
            {c.cta_secondary?.label && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                type="button"
                className={ctaClasses(s.cta_style || 'filled', accent, 'secondary')}
                style={{
                  ...ctaInlineStyle(s.cta_style || 'filled', accent, 'secondary'),
                  padding: isMobile ? '10px 16px' : undefined,
                }}
              >
                {c.cta_secondary.label}
                <ArrowRight style={{ width: isMobile ? 13 : 16, height: isMobile ? 13 : 16 }} />
              </motion.button>
            )}
          </motion.div>
        )}
      </div>

      {/* Device frame indicator (visible only in non-compact) */}
      {!compact && (
        <div className="absolute top-3 right-3 z-10">
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/55 backdrop-blur text-[10px] text-white/80 uppercase tracking-wider font-semibold">
            <span className={`w-1.5 h-1.5 rounded-full ${isMobile ? 'bg-pink-400' : 'bg-emerald-400'}`} />
            {isMobile ? 'Telefon' : 'Komputer'}
          </div>
        </div>
      )}
    </div>
  );
}
